import { useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';

import { useAuth } from '../../../auth/AuthProvider';
import { permissions } from '../../../auth/permissions';
import { useConversations } from '../../conversations/api/useConversations';
import type { ConversationDto } from '../../conversations/types/conversation';
import { useDocuments } from '../../documents/api/useDocuments';
import { useInvestigations } from '../../investigations/api/useInvestigations';
import type { InvestigationDto } from '../../investigations/types/investigation';
import { useMachineMemory } from '../../machine-memory/api/useMachineMemory';
import type { MachineWorkspaceResponseDto } from '../../machine-memory/types/machine-memory';
import { useFacilities } from '../../organization/api/useFacilities';
import { useArchiveMachine } from '../api/useMachineLifecycle';
import { machineLifecycleGuidance } from '../lifecycle-guidance';
import { useMachines } from '../api/useMachines';
import { MachineLifecycleDialog } from '../components/MachineLifecycleDialog';

const FIRST_QUESTION_SUGGESTIONS = [
  'What are the most common maintenance issues?',
  'What patterns appear in the maintenance history?',
  'What does the available evidence say I should pay attention to?',
] as const;

/**
 * The machine itself is the anchor for Route42 work. This route is therefore a
 * real machine home rather than a conversation redirect. Explicit legacy
 * prompt/document deep links still enter a fresh conversation directly.
 */
export function MachineEntryPage() {
  const { machineId = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const auth = useAuth();
  const [archiveConfirmationOpen, setArchiveConfirmationOpen] = useState(false);
  const archiveMachine = useArchiveMachine();
  const params = new URLSearchParams(location.search);
  const hasNewConversationContext = params.has('prompt') || params.has('document');

  const machines = useMachines();
  const facilities = useFacilities();
  const canReadDocuments = auth.can(permissions.documentsRead);
  const canReadConversations = auth.can(permissions.conversationRead);
  const canReadInvestigations = auth.can(permissions.investigationRead);
  const canAsk = canReadConversations
    && auth.can(permissions.conversationCreate)
    && auth.can(permissions.questionAsk);
  const documents = useDocuments(machineId, canReadDocuments);
  const conversations = useConversations(machineId, canReadConversations);
  const investigations = useInvestigations(machineId, canReadInvestigations);
  const memory = useMachineMemory(machineId, auth.can(permissions.machineRead));

  if (!machineId) return <Navigate to="/machines" replace />;
  if (!auth.can(permissions.machineRead)) return <Navigate to="/machines" replace />;
  if (hasNewConversationContext) {
    return <Navigate to={`/machines/${machineId}/conversations/new${location.search}`} replace />;
  }

  if (machines.isPending) return <MachineOverviewLoading />;
  if (machines.isError) {
    return (
      <section className="r42-page r42-page--wide">
        <div className="r42-inline-state r42-inline-state--error">
          <span>Route42 couldn't load this machine.</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={() => void machines.refetch()}>Try again</button>
        </div>
      </section>
    );
  }

  const machine = machines.data?.machines.find((item) => item.id === machineId);
  if (!machine) return <Navigate to="/machines" replace />;

  const facility = facilities.data?.facilities.find((item) => item.facility_id === machine.facilityId) ?? null;
  const evidence = documents.data?.documents ?? [];
  const evidenceCount = evidence.length;
  const readyEvidenceCount = evidence.filter((item) => item.questionReady).length;
  const learningEvidenceCount = evidence.filter((item) => item.uiStatus === 'learning' || item.uiStatus === 'uploading').length;
  const visibleConversations = (conversations.data?.conversations ?? []).filter((item) => Number(item.turn_count ?? 0) > 0);
  const conversationCount = visibleConversations.length;
  const exchangeCount = visibleConversations.reduce((total, item) => total + Number(item.turn_count ?? 0), 0);
  const allInvestigations = investigations.data?.investigations ?? [];
  const activeInvestigations = allInvestigations.filter((item) => !['CLOSED', 'ARCHIVED'].includes(item.status));
  const recentConversation = mostRecentConversation(visibleConversations);
  const recentInvestigation = mostRecentInvestigation(activeInvestigations);
  const recentWork = chooseRecentWork(recentConversation, recentInvestigation);
  const memoryState = machineMemorySummary(memory.data, memory.isLoading, evidenceCount);
  const workQueriesLoading = (canReadDocuments && documents.isLoading)
    || (canReadConversations && conversations.isLoading)
    || (canReadInvestigations && investigations.isLoading);

  const machineDescriptor = [machine.machineType, machine.manufacturer, machine.model].filter(Boolean).join(' · ');
  const areaAndLine = [machine.productionArea, machine.productionLine].filter(Boolean).join(' · ');
  const machineLocation = machine.location ?? facility?.location ?? null;
  const archiveError = archiveMachine.error instanceof Error ? archiveMachine.error.message : null;
  const machineArchiveId = machine.id;
  const machineArchiveName = machine.name;

  async function confirmArchive() {
    try {
      await archiveMachine.mutateAsync(machineArchiveId);
      navigate('/machines?status=archived', {
        replace: true,
        state: {
          machineLifecycleNotice: machineLifecycleGuidance('archive', machineArchiveName),
        },
      });
    } catch {
      // Mutation state keeps the dialog open with a safe retry message.
    }
  }

  return (
    <section className="r42-page r42-page--wide r42-machine-home">
      <header className="r42-page-header r42-machine-home__header">
        <div>
          <span className="r42-eyebrow">Machine</span>
          <h1>{machine.name}</h1>
          <p>Your home for this machine's evidence, conversations, investigations, and Machine Memory.</p>
        </div>
        <div className="r42-machine-home__header-actions">
          <span className="r42-machine-home__status"><span aria-hidden="true" /> Active</span>
          {auth.can(permissions.machineArchive) ? (
            <button className="btn btn-sm btn-outline-secondary r42-machine-archive-action" type="button" onClick={() => setArchiveConfirmationOpen(true)}>
              <i className="bi bi-archive" aria-hidden="true" /> Archive machine
            </button>
          ) : null}
        </div>
      </header>

      <section className="r42-machine-overview-hero" aria-label="Machine identity">
        <div className="r42-machine-overview-identity">
          <div className="r42-machine-overview-icon"><i className="bi bi-gear-wide-connected" aria-hidden="true" /></div>
          <div>
            <span className="r42-eyebrow">{machine.assetId ? `Asset ${machine.assetId}` : 'Machine identity'}</span>
            <h2>{machine.machineType ?? machine.name}</h2>
            <p>{machineDescriptor || 'Machine details can be added as they become useful.'}</p>
          </div>
        </div>
        <dl className="r42-machine-overview-facts">
          <MachineFact label="Facility" value={facility?.facility_name ?? (machine.facilityId ? 'Assigned facility' : 'Not specified')} />
          <MachineFact label="Area / line" value={areaAndLine || 'Not specified'} />
          <MachineFact label="Location" value={machineLocation ?? 'Not specified'} />
          <MachineFact label="Status" value={<><span className="r42-machine-status-dot" aria-hidden="true" /> Active</>} />
        </dl>
      </section>

      <div className="r42-machine-summary-grid" aria-label="Machine workspace summary">
        <MachineSummaryCard
          to={canReadDocuments ? `/machines/${machineId}/documents` : null}
          icon="bi-file-earmark-text"
          value={canReadDocuments ? queryCount(documents.isLoading, evidenceCount) : '—'}
          label="Evidence sources"
          detail={canReadDocuments ? evidenceDetail(readyEvidenceCount, learningEvidenceCount) : 'Available based on your access'}
        />
        <MachineSummaryCard
          to={canReadConversations ? `/machines/${machineId}/conversations` : null}
          icon="bi-chat-left-text"
          value={canReadConversations ? queryCount(conversations.isLoading, conversationCount) : '—'}
          label="Conversations"
          detail={canReadConversations ? `${exchangeCount} ${exchangeCount === 1 ? 'exchange' : 'exchanges'}` : 'Available based on your access'}
        />
        <MachineSummaryCard
          to={canReadInvestigations ? `/machines/${machineId}/investigations` : null}
          icon="bi-search"
          value={canReadInvestigations ? queryCount(investigations.isLoading, allInvestigations.length) : '—'}
          label="Investigations"
          detail={canReadInvestigations ? `${activeInvestigations.length} active` : 'Available based on your access'}
        />
        <MachineSummaryCard
          to={`/machines/${machineId}/memory`}
          icon="bi-cpu"
          value={memoryState.value}
          label="Machine Memory"
          detail={memoryState.detail}
        />
      </div>

      <MachineNextAction
        machineId={machineId}
        loading={workQueriesLoading}
        recentWork={recentWork}
        evidenceCount={evidenceCount}
        readyEvidenceCount={readyEvidenceCount}
        learningEvidenceCount={learningEvidenceCount}
        canReadDocuments={canReadDocuments}
        canUploadDocuments={auth.can(permissions.documentsUpload)}
        canAsk={canAsk}
      />

      {archiveConfirmationOpen ? (
        <MachineLifecycleDialog
          action="archive"
          machine={machine}
          busy={archiveMachine.isPending}
          error={archiveError}
          onCancel={() => { if (!archiveMachine.isPending) setArchiveConfirmationOpen(false); }}
          onConfirm={() => void confirmArchive()}
        />
      ) : null}
    </section>
  );
}

function MachineFact({ label, value }: { label: string; value: ReactNode }) {
  return <div><dt>{label}</dt><dd>{value}</dd></div>;
}

function MachineSummaryCard({
  to,
  icon,
  value,
  label,
  detail,
}: {
  to: string | null;
  icon: string;
  value: string | number;
  label: string;
  detail: string;
}) {
  const content = (
    <>
      <span className="r42-machine-summary-card__icon"><i className={`bi ${icon}`} aria-hidden="true" /></span>
      <strong>{value}</strong>
      <span>{label}</span>
      <small>{detail}</small>
      {to ? <i className="bi bi-arrow-right r42-machine-summary-card__arrow" aria-hidden="true" /> : null}
    </>
  );

  return to
    ? <Link className="r42-machine-summary-card" to={to}>{content}</Link>
    : <div className="r42-machine-summary-card r42-machine-summary-card--static">{content}</div>;
}

type RecentWork =
  | { kind: 'conversation'; title: string; href: string; activityAt: string | null; turnCount: number }
  | { kind: 'investigation'; title: string; href: string; activityAt: string | null; status: string };

function MachineNextAction({
  machineId,
  loading,
  recentWork,
  evidenceCount,
  readyEvidenceCount,
  learningEvidenceCount,
  canReadDocuments,
  canUploadDocuments,
  canAsk,
}: {
  machineId: string;
  loading: boolean;
  recentWork: RecentWork | null;
  evidenceCount: number;
  readyEvidenceCount: number;
  learningEvidenceCount: number;
  canReadDocuments: boolean;
  canUploadDocuments: boolean;
  canAsk: boolean;
}) {
  if (loading) {
    return (
      <section className="r42-machine-work-card r42-machine-work-card--loading" role="status" aria-live="polite">
        <span className="r42-learning-dot" aria-hidden="true" />
        <div><strong>Finding your best place to continue…</strong><span>Route42 is checking this machine's current workspace.</span></div>
      </section>
    );
  }

  if (recentWork?.kind === 'investigation') {
    return (
      <section className="r42-machine-work-card">
        <div className="r42-machine-work-card__icon"><i className="bi bi-search" aria-hidden="true" /></div>
        <div className="r42-machine-work-card__body">
          <span className="r42-eyebrow">Continue working</span>
          <h2>{recentWork.title}</h2>
          <p>Return to the machine problem your team has been working over time.</p>
          <span className="r42-machine-work-card__meta">Investigation · {humanStatus(recentWork.status)} · Updated {formatRelativeTime(recentWork.activityAt)}</span>
        </div>
        <Link className="btn r42-btn-primary" to={recentWork.href}>Continue investigation</Link>
      </section>
    );
  }

  if (recentWork?.kind === 'conversation') {
    return (
      <section className="r42-machine-work-card">
        <div className="r42-machine-work-card__icon"><i className="bi bi-chat-left-text" aria-hidden="true" /></div>
        <div className="r42-machine-work-card__body">
          <span className="r42-eyebrow">Continue working</span>
          <h2>{recentWork.title}</h2>
          <p>Pick up the latest grounded conversation for this machine.</p>
          <span className="r42-machine-work-card__meta">{recentWork.turnCount} {recentWork.turnCount === 1 ? 'exchange' : 'exchanges'} · Updated {formatRelativeTime(recentWork.activityAt)}</span>
        </div>
        <Link className="btn r42-btn-primary" to={recentWork.href}>Continue conversation</Link>
      </section>
    );
  }

  if (evidenceCount === 0) {
    return (
      <section className="r42-machine-work-card r42-machine-work-card--empty">
        <div className="r42-machine-work-card__icon"><i className="bi bi-file-earmark-arrow-up" aria-hidden="true" /></div>
        <div className="r42-machine-work-card__body">
          <span className="r42-eyebrow">Best next step</span>
          <h2>Give Route42 something to learn from.</h2>
          <p>Add a manual, maintenance history, inspection, technician note, spreadsheet, or machine image so this workspace can start building grounded understanding.</p>
        </div>
        {canUploadDocuments ? <Link className="btn r42-btn-primary" to={`/machines/${machineId}/documents/add`}>Add evidence</Link> : canReadDocuments ? <Link className="btn btn-outline-secondary" to={`/machines/${machineId}/documents`}>View documents</Link> : null}
      </section>
    );
  }

  if (readyEvidenceCount > 0 && canAsk) {
    return (
      <section className="r42-machine-work-card r42-machine-work-card--question">
        <div className="r42-machine-work-card__icon"><i className="bi bi-stars" aria-hidden="true" /></div>
        <div className="r42-machine-work-card__body">
          <span className="r42-eyebrow">Best next step</span>
          <h2>Ask the first question.</h2>
          <p>{readyEvidenceCount} {readyEvidenceCount === 1 ? 'source is' : 'sources are'} ready for grounded questions. Start with something your team would normally have to answer by searching through machine history.</p>
          <div className="r42-machine-question-suggestions" aria-label="Suggested first questions">
            {FIRST_QUESTION_SUGGESTIONS.map((question) => (
              <Link key={question} to={`/machines/${machineId}/conversations/new?prompt=${encodeURIComponent(question)}`}>{question}</Link>
            ))}
          </div>
        </div>
        <Link className="btn r42-btn-primary" to={`/machines/${machineId}/conversations/new`}>Ask a question</Link>
      </section>
    );
  }

  if (readyEvidenceCount === 0 && canReadDocuments) {
    return (
      <section className="r42-machine-work-card r42-machine-work-card--empty">
        <div className="r42-machine-work-card__icon"><i className="bi bi-arrow-repeat" aria-hidden="true" /></div>
        <div className="r42-machine-work-card__body">
          <span className="r42-eyebrow">Route42 is learning</span>
          <h2>Grounded questions will activate when usable evidence is ready.</h2>
          <p>{learningEvidenceCount > 0 ? `${learningEvidenceCount} ${learningEvidenceCount === 1 ? 'source is' : 'sources are'} still being processed.` : 'Open Documents to review the current evidence status and any action Route42 needs from you.'}</p>
        </div>
        <Link className="btn btn-outline-secondary" to={`/machines/${machineId}/documents`}>Check evidence status</Link>
      </section>
    );
  }

  return (
    <section className="r42-machine-work-card r42-machine-work-card--empty">
      <div className="r42-machine-work-card__icon"><i className="bi bi-compass" aria-hidden="true" /></div>
      <div className="r42-machine-work-card__body">
        <span className="r42-eyebrow">Machine workspace</span>
        <h2>Choose the part of this machine you want to understand.</h2>
        <p>Your role determines which evidence, conversations, investigations, and machine intelligence you can open.</p>
      </div>
    </section>
  );
}

function mostRecentConversation(items: ConversationDto[]): ConversationDto | null {
  return [...items].sort((left, right) => activityTime(right.updated_at ?? right.created_at) - activityTime(left.updated_at ?? left.created_at))[0] ?? null;
}

function mostRecentInvestigation(items: InvestigationDto[]): InvestigationDto | null {
  return [...items].sort((left, right) => activityTime(right.updated_at ?? right.created_at) - activityTime(left.updated_at ?? left.created_at))[0] ?? null;
}

function chooseRecentWork(conversation: ConversationDto | null, investigation: InvestigationDto | null): RecentWork | null {
  const conversationWork: RecentWork | null = conversation ? {
    kind: 'conversation',
    title: conversation.title?.trim() || 'Machine conversation',
    href: `/machines/${conversation.machine_id}/conversations/${conversation.conversation_id}`,
    activityAt: conversation.updated_at ?? conversation.created_at ?? null,
    turnCount: Number(conversation.turn_count ?? 0),
  } : null;
  const investigationWork: RecentWork | null = investigation ? {
    kind: 'investigation',
    title: investigation.title,
    href: `/machines/${investigation.machine_id}/investigations/${investigation.investigation_id}`,
    activityAt: investigation.updated_at ?? investigation.created_at ?? null,
    status: investigation.status,
  } : null;

  if (!conversationWork) return investigationWork;
  if (!investigationWork) return conversationWork;
  return activityTime(investigationWork.activityAt) >= activityTime(conversationWork.activityAt) ? investigationWork : conversationWork;
}

function machineMemorySummary(
  workspace: MachineWorkspaceResponseDto | undefined,
  loading: boolean,
  evidenceCount: number,
): { value: string; detail: string } {
  if (loading) return { value: '—', detail: 'Checking current memory' };
  if (workspace) {
    return {
      value: 'Current',
      detail: workspace.machine_memory.updated_at ? `Updated ${formatRelativeTime(workspace.machine_memory.updated_at)}` : 'Persistent understanding available',
    };
  }
  return evidenceCount > 0
    ? { value: 'Building', detail: 'Route42 is still developing memory' }
    : { value: 'Not started', detail: 'Add evidence to begin' };
}

function evidenceDetail(ready: number, learning: number): string {
  if (ready > 0 && learning > 0) return `${ready} ready · ${learning} learning`;
  if (ready > 0) return `${ready} ready for questions`;
  if (learning > 0) return `${learning} learning`;
  return 'No grounded source ready yet';
}

function queryCount(loading: boolean, count: number): string | number {
  return loading ? '—' : count;
}

function activityTime(value?: string | null): number {
  if (!value) return 0;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : 0;
}

function formatRelativeTime(value?: string | null): string {
  const timestamp = activityTime(value);
  if (!timestamp) return 'recently';
  const diff = Date.now() - timestamp;
  if (diff < 60_000) return 'just now';
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} ${hours === 1 ? 'hour' : 'hours'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} ${days === 1 ? 'day' : 'days'} ago`;
  const date = new Date(timestamp);
  return new Intl.DateTimeFormat(undefined, { month: 'short', day: 'numeric', year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric' }).format(date);
}

function humanStatus(value: string): string {
  if (value === 'PAUSED') return 'On hold';
  if (value === 'DRAFT') return 'Open';
  return value.toLocaleLowerCase().replace(/\b\w/g, (letter) => letter.toLocaleUpperCase());
}

function MachineOverviewLoading() {
  return (
    <section className="r42-page r42-page--wide r42-machine-home">
      <div className="r42-machine-home-loading" role="status" aria-live="polite">
        <span className="r42-learning-dot" aria-hidden="true" />
        <div><strong>Opening machine…</strong><span>Route42 is preparing the machine overview.</span></div>
      </div>
    </section>
  );
}
